import jwt from "jwt-decode";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../services/api";

interface AuthProviderProps {
  children: ReactNode;
}

interface Sprite {
  id: string;
  key: string | null;
  name: string;
  url: string;
}

interface Status {
  agility: number;
  defense: number;
  hp: number;
  id: string;
  level: number;
  magic: number;
  points: number;
  strength: number;
  vigor: number;
}

interface Attack {
  id: string;
  name: string;
  power: number;
  type: string;
  hits: number;
  accuracy: number;
}

interface Char {
  attacks: Attack[];
  id: string;
  name: string;
  token: string | null;
  sprite: Sprite;
  status: Status;
}
interface User {
  id: string;
  username: string;
  email: string;
  password?: string;
  active: boolean;
  adm?: boolean;
  char: Char;
  user: any;
}

interface iAuth {
  accessToken: string;
  user: User;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface iSignUpData {
  username: string;
  email: string;
  password: string;
}

interface IDecoded {
  id: string;
  iat: number;
  exp: number;
}

interface AuthContextData {
  user: User;
  accessToken: string;
  signIn: (credentials: SignInCredentials) => Promise<void>;
  signUp: (signUpData: iSignUpData) => Promise<void>;
  signOut: () => void;
  updateUser: (
    signUpdata: iSignUpData,
    accessToken: string,
    userId: string
  ) => Promise<void>;
  initBattle: (accessToken: string, stage: number) => Promise<void>;
  battleInfo: any;
  handleAttack: (attackId: string, accessToken: string) => Promise<void>;
  uLife: number;
  eLife: number;
  findUser: (userId: string, token: string) => Promise<void>;
  leaderboard: any;
  getLeaderboard: (accessToken: string) => Promise<void>;
  createChar: (name: string, spriteId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [battleInfo, setBattleInfo] = useState<any>({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [uLife, setuLife] = useState<number>(0);
  const [eLife, seteLife] = useState<number>(0);
  const [data, setData] = useState<iAuth>(() => {
    const accessToken = localStorage.getItem("@game:accessToken");
    const user = localStorage.getItem("@game:user");

    if (accessToken && user) {
      return { accessToken, user: JSON.parse(user) };
    }

    return {} as iAuth;
  });

  const history = useHistory();

  const findUser = useCallback(
    async (userId: string, token: string) => {
      await api
        .get(`/users/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          localStorage.setItem(
            "@game:user",
            JSON.stringify(response.data.user)
          );
          setData({ accessToken: token, user: response.data.user });
          if (!response.data.user.char) {
            history.push("/create_char");
          }
        })
        .catch((err) => {
          console.log(err);
        });
    },
    [history]
  );

  const signIn = useCallback(
    async ({ email, password }: SignInCredentials) => {
      const response = await api.post("/users/login", { email, password });

      const { token } = response.data;

      localStorage.setItem("@game:accessToken", token);
      const userId: IDecoded = jwt(token);
      await findUser(userId.id, token);
    },
    [findUser]
  );

  const createChar = useCallback(
    async (name: string, spriteId: string) => {
      await api
        .post(
          "/chars",
          { name, spriteId },
          {
            headers: {
              Authorization: `Bearer ${data.accessToken}`,
            },
          }
        )
        .then(async (_) => {
          await findUser(data.user.id, data.accessToken);
          history.push("/dashboard");
        })
        .catch((err) => {
          toast.error("Char name already in use");
        });
    },
    [data, findUser, history]
  );

  const signUp = useCallback(
    async (signUpdata: iSignUpData) => {
      await api
        .post("/users/register", signUpdata)
        .then((_) => {
          toast.success("Cadastro realizado com sucesso! faça login");
          history.push("/login");
        })
        .catch((err) => {
          toast.error("Email já cadastrado!");
          console.log(err);
        });
    },
    [history]
  );

  const updateUser = useCallback(
    async (signUpdata: iSignUpData, accessToken: string, userId: string) => {
      await api
        .patch(`/users/${userId}`, signUpdata, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then((response) => {
          toast.success("Informações editadas com sucesso!");

          localStorage.setItem("@game:accessToken", accessToken);
          localStorage.setItem("@game:user", JSON.stringify(response.data));

          setData({ accessToken, user: response.data });
        })
        .catch((err) => {
          toast.error("Tente novamente!");
          console.log(err);
        });
    },
    []
  );

  const initBattle = useCallback(
    async (accessToken: string, stage: number) => {
      await api
        .get(`/battle/${stage}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        })
        .then((response) => {
          toast.success("Iniciando batalha!");
          history.push(`battle/${stage}`);
          setBattleInfo(response.data);
          setuLife(response.data.char.status.hp);
          seteLife(response.data.enemy.status.hp);
        })
        .catch((err) => {
          toast.error("Tente novamente!");
          console.log(err);
        });
    },
    [history]
  );

  const handleAttack = useCallback(
    async (attackId: string, accessToken: string) => {
      await api
        .post(
          `/battle`,
          {
            attackId: attackId,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        )
        .then((response) => {
          setBattleInfo(response.data);
          console.log(response.data);
        })
        .catch((err) => {
          toast.error("Tente novamente!");
          console.log(err);
        });
    },
    []
  );

  const getLeaderboard = useCallback(async (accessToken: string) => {
    await api
      .get(`/chars/leaderboard`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((response) => {
        setLeaderboard(response.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem("@game:accessToken");
    localStorage.removeItem("@game:user");

    setData({} as iAuth);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        accessToken: data.accessToken,
        signIn,
        signUp,
        signOut,
        updateUser,
        initBattle,
        battleInfo,
        handleAttack,
        uLife,
        eLife,
        findUser,
        leaderboard,
        getLeaderboard,
        createChar,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
